/**
 * aggregations.ts — VIREON Cortex Time-Window Aggregation Layer
 * ==============================================================
 * Provides window selection, bucketing, ToD energy breakdown, and
 * cost aggregation over arrays of TelemetryRow.
 *
 * All derived values (pfAvg, kvaTotal, neutralCurrentA, etc.) are already
 * computed by derivedEngine.ts — this module only aggregates them.
 */

import { format, startOfDay, startOfWeek, startOfMonth } from 'date-fns';
import { percentile } from './calculations';
import type {
  AggregateBucket,
  CostBreakdownItem,
  EnergyByTodItem,
  EnergyPeriod,
  Granularity,
  PenaltyDetails,
  PfEvent,
  TelemetryRow,
} from '@/types';
import {
  capacityFactor,
  costRupees,
  energyDelivered,
  isValidPfReading,
  loadFactor,
  max,
  maxRisk,
  mean,
  min,
  detectAnomalies,
  todMix,
  safe,
  powerFactor,
} from '@/lib/calculations';
import {
  PF_SUSTAINED_FLOOR,
  TOD_WINDOWS,
  pfAdjustmentPct,
  type ToDPeriod,
} from '@/config/constants';

/* -------------------------------------------------------------------------- */
/*  Sensor quality filter                                                     */
/*                                                                            */
/*  Voltage: drop if below hard floor (100 V = sensor dropout) OR above      */
/*           the 99.5th-percentile ceiling (upward spike).                   */
/*  Power:   ceiling only (99.5th pct). Floor = 0 is valid during idle.      */
/* -------------------------------------------------------------------------- */

const VOLTAGE_MIN_V = 100;

const VOLTAGE_FIELDS = [
  'vR', 'vY', 'vB', 'vlnAvg', 'vllAvg',
] as const satisfies readonly (keyof TelemetryRow)[];

// Use new canonical field names; backward-compat aliases are also present
const POWER_FIELDS = [
  'kwTotal',  'kvaTotal',
  'currentR', 'currentY', 'currentB', 'currentTotal',
  'neutralCurrentA',
  'voltageUnbalancePct', 'currentUnbalancePct',
  'energyKwh',
] as const satisfies readonly (keyof TelemetryRow)[];

export function sanitiseWindow(rows: TelemetryRow[]): TelemetryRow[] {
  if (rows.length < 10) return rows;

  const voltageCeilings: Partial<Record<string, number>> = {};
  for (const field of VOLTAGE_FIELDS) {
    const vals = rows.map((r) => r[field] as number).filter(Number.isFinite);
    if (!vals.length) continue;
    voltageCeilings[field] = percentile(vals, 99.5);
  }

  const powerCeilings: Partial<Record<string, number>> = {};
  for (const field of POWER_FIELDS) {
    const vals = rows.map((r) => r[field] as number).filter(Number.isFinite);
    if (!vals.length) continue;
    powerCeilings[field] = percentile(vals, 99.5);
  }

  return rows.filter((row) => {
    for (const field of VOLTAGE_FIELDS) {
      const v = row[field] as number;
      if (!Number.isFinite(v)) continue;
      if (v < VOLTAGE_MIN_V) return false;
      const ceiling = voltageCeilings[field];
      if (ceiling !== undefined && v > ceiling) return false;
    }
    for (const field of POWER_FIELDS) {
      const v = row[field] as number;
      if (!Number.isFinite(v)) continue;
      const ceiling = powerCeilings[field];
      if (ceiling !== undefined && v > ceiling) return false;
    }
    return true;
  });
}

/* -------------------------------------------------------------------------- */
/*  Window selection                                                          */
/* -------------------------------------------------------------------------- */

export function windowFor(rows: TelemetryRow[], g: Granularity): TelemetryRow[] {
  if (!rows.length) return [];
  // Anchor to the last data row, not the wall clock (see buildKpiSummary).
  const now = rows[rows.length - 1].timestamp;
  let fromTs: number;
  let toTs: number;

  if (g === 'hourly') {
    const latest = rows[rows.length - 1].timestamp.getTime();
    fromTs = latest - 24 * 3_600_000;
    toTs   = latest;
  } else if (g === 'daily') {
    // BUG FIX: toTs was yesterday 23:59:59, hard-excluding today.
    // Changed to now so the daily/weekly window includes today's partial data.
    const today  = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sunday = new Date(today.getTime() - today.getDay() * 24 * 3_600_000);
    fromTs = sunday.getTime();
    toTs   = now.getTime();
  } else {
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999);
    fromTs = yesterday.getTime() - 29 * 24 * 3_600_000;
    toTs   = yesterday.getTime();
  }

  return sanitiseWindow(
    rows.filter((r) => { const ts = r.timestamp.getTime(); return ts >= fromTs && ts <= toTs; }),
  );
}

export function windowLabel(g: Granularity): string {
  return g === 'hourly' ? 'last 24 hours'
       : g === 'daily'  ? 'this week (Sun – yesterday)'
       : 'last 30 days';
}

/* -------------------------------------------------------------------------- */
/*  Energy window by period                                                   */
/* -------------------------------------------------------------------------- */

export function energyWindowFor(
  rows: TelemetryRow[],
  period: EnergyPeriod,
  customRange?: { from: Date; to: Date },
): TelemetryRow[] {
  if (!rows.length) return [];
  // Anchor to the last data row, not the wall clock (see buildKpiSummary).
  const now  = rows[rows.length - 1].timestamp;
  const from = (() => {
    switch (period) {
      case 'today':     return startOfDay(now);
      case 'thisWeek':  return startOfWeek(now, { weekStartsOn: 0 });
      case 'thisMonth': return startOfMonth(now);
      case 'custom':    return customRange?.from ?? startOfDay(now);
    }
  })();
  const to   = period === 'custom' && customRange?.to ? customRange.to : now;
  return sanitiseWindow(rows.filter((r) => r.timestamp >= from && r.timestamp <= to));
}

export function energyPeriodLabel(period: EnergyPeriod): string {
  switch (period) {
    case 'today':     return 'today';
    case 'thisWeek':  return 'this week';
    case 'thisMonth': return 'this month';
    case 'custom':    return 'custom range';
  }
}

/* -------------------------------------------------------------------------- */
/*  Bucketing utilities                                                       */
/* -------------------------------------------------------------------------- */

function bucketKey(d: Date, g: Granularity): string {
  if (g === 'hourly')  return format(d, 'yyyy-MM-dd HH');
  if (g === 'daily')   return format(d, 'yyyy-MM-dd');
  return format(d, 'yyyy-MM');
}

function bucketLabel(key: string, g: Granularity): string {
  if (g === 'hourly') {
    const [, hh] = key.split(' ');
    return `${hh}:00`;
  }
  if (g === 'daily') return format(new Date(`${key}T00:00:00`), 'd MMM');
  return format(new Date(`${key}-01T00:00:00`), 'MMM yy');
}

export function bucketize(rows: TelemetryRow[], granularity: Granularity): AggregateBucket[] {
  if (!rows.length) return [];

  const groups = new Map<string, TelemetryRow[]>();
  for (const r of rows) {
    const k    = bucketKey(r.timestamp, granularity);
    const list = groups.get(k);
    if (list) list.push(r);
    else groups.set(k, [r]);
  }

  const raw: (AggregateBucket & { _rawKwh: number })[] = [];
  for (const [key, bucketRows] of groups) {
    const kws     = bucketRows.map((r) => safe(r.kwTotal));
    const flagged = bucketRows.filter((r) => detectAnomalies(r).length > 0).length;
    raw.push({
      key,
      label:   bucketLabel(key, granularity),
      ts:      bucketRows[0].timestamp.getTime(),
      kwAvg:   mean(kws),
      kwMin:   min(kws),
      kwMax:   max(kws),
      kvaAvg:  mean(bucketRows.map((r) => safe(r.kvaTotal))),
      pfAvg:   mean(bucketRows.map((r) => powerFactor(r))),
      kwh:     energyDelivered(bucketRows),
      costRs:  costRupees(bucketRows),
      neutralCurrentMax:   max(bucketRows.map((r) => safe(r.neutralCurrentA))),
      voltageUnbalanceMax: max(bucketRows.map((r) => safe(r.voltageUnbalancePct))),
      currentUnbalanceMax: max(bucketRows.map((r) => safe(r.currentUnbalancePct))),
      riskMax:     maxRisk(bucketRows.map((r) => r.fireRiskLevel)),
      anomalyPct:  (flagged / bucketRows.length) * 100,
      todMix:      todMix(bucketRows),
      samples:     bucketRows.length,
      _rawKwh:     energyDelivered(bucketRows),
    });
  }

  // Clamp per-bucket kWh to 99.5th-percentile to catch register resets
  if (raw.length >= 4) {
    const nonZero = raw.map((b) => b._rawKwh).filter((v) => v > 0);
    if (nonZero.length) {
      const kwhCeiling = percentile(nonZero, 99.5);
      if (kwhCeiling > 0) {
        for (const b of raw) { if (b.kwh > kwhCeiling) b.kwh = kwhCeiling; }
      }
    }
  }

  return raw
    .map(({ _rawKwh: _d, ...rest }) => rest as AggregateBucket)
    .sort((a, b) => a.ts - b.ts);
}

/* -------------------------------------------------------------------------- */
/*  Energy breakdown by Time-of-Day                                          */
/* -------------------------------------------------------------------------- */

/**
 * Per-period energy breakdown. For Telangana (kVAh billing) pass useKvah=true
 * so that energies are computed from the VAh register delta (same method as
 * buildBillBreakdown) rather than the kW trapezoidal integral.
 */
export function energyByTodPeriod(rows: TelemetryRow[], useKvah = false): EnergyByTodItem[] {
  const periods: ToDPeriod[] = ['OFF-PEAK', 'NORMAL', 'PEAK'];

  /** kVAh delivered in a period window via VAh register deltas (Telangana). */
  function kvahDeliveredLocal(subset: TelemetryRow[]): number {
    if (subset.length < 2) return 0;
    const first = subset[0];
    const last  = subset[subset.length - 1];
    return Math.max(0, (last.energyVAh ?? 0) - (first.energyVAh ?? 0)) / 1000;
  }

  const totals = periods.map((p) => {
    const subset = rows
      .filter((r) => r.todPeriod === p)
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const energy = useKvah ? kvahDeliveredLocal(subset) : energyDelivered(subset);
    return {
      todPeriod: p,
      label:     `${TOD_WINDOWS[p].label} · ${TOD_WINDOWS[p].range}`,
      kwh:       energy,
      costRs:    costRupees(subset),
    };
  });
  const totalKwh = totals.reduce((acc, t) => acc + t.kwh, 0) || 1;
  return totals.map((t) => ({ ...t, pctOfDay: (t.kwh / totalKwh) * 100 }));
}

export function costBreakdown(rows: TelemetryRow[]): CostBreakdownItem[] {
  const periods: ToDPeriod[] = ['OFF-PEAK', 'NORMAL', 'PEAK'];
  const totals = periods.map((p) => {
    const subset = rows.filter((r) => r.todPeriod === p)
      .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    return { todPeriod: p, kwh: energyDelivered(subset), costRs: costRupees(subset) };
  });
  const totalCost = totals.reduce((acc, t) => acc + t.costRs, 0) || 1;
  return totals.map((t) => ({ ...t, pctOfDay: (t.costRs / totalCost) * 100 }));
}

/* -------------------------------------------------------------------------- */
/*  PF Penalty / Rebate evaluation                                            */
/* -------------------------------------------------------------------------- */

export function evaluatePenalty(rows: TelemetryRow[]): PenaltyDetails {
  const empty: PenaltyDetails = {
    applies: false, rebateApplies: false,
    pfAvg: 0, pfMin: 0,
    estimatedExtraRs: 0, estimatedRebateRs: 0,
    adjustmentPct: 0,
    sustainedEvents: 0, sustainedMinutes: 0,
    events: [],
  };
  if (!rows.length) return empty;

  const validRows = rows
    .filter(isValidPfReading)
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  if (!validRows.length) return empty;

  const pfs    = validRows.map(powerFactor).filter((v) => v > 0);
  if (!pfs.length) return empty;

  const pfAvg    = mean(pfs);
  const pfMin    = min(pfs);
  const baseCost = costRupees(rows);

  // Sustained low-PF events (≥2 consecutive readings < PF_SUSTAINED_FLOOR)
  const events: PfEvent[] = [];
  let runStart = -1;
  for (let i = 0; i <= validRows.length; i++) {
    const pf  = i < validRows.length ? powerFactor(validRows[i]) : 1;
    const low = pf > 0 && pf < PF_SUSTAINED_FLOOR;
    if (low && runStart === -1) runStart = i;
    if ((!low || i === validRows.length) && runStart !== -1) {
      const len  = i - runStart;
      if (len >= 2) {
        const slice    = validRows.slice(runStart, i);
        const start    = slice[0].timestamp;
        const end      = slice[slice.length - 1].timestamp;
        const slicePfs = slice.map(powerFactor);
        events.push({
          date:        format(start, 'yyyy-MM-dd'),
          start:       format(start, 'HH:mm'),
          end:         format(end,   'HH:mm'),
          readings:    len,
          durationMin: Math.max(1, Math.round((end.getTime() - start.getTime()) / 60_000)),
          minPf:       min(slicePfs),
          avgPf:       mean(slicePfs),
          location:    slice[0].location || slice[0].meterSerial || '—',
        });
      }
      runStart = -1;
    }
  }

  const sustainedMinutes = events.reduce((acc, e) => acc + e.durationMin, 0);
  const blendedAdjPct    = pfAdjustmentPct(pfAvg, 'NORMAL');

  let estimatedExtraRs   = 0;
  let estimatedRebateRs  = 0;
  let adjustmentPct      = 0;
  let applies            = false;
  let rebateApplies      = false;

  if (blendedAdjPct < 0) {
    const pct = Math.abs(blendedAdjPct);
    adjustmentPct    = pct;
    estimatedExtraRs = baseCost * (pct / 100);
    applies          = estimatedExtraRs > 1;
  } else if (blendedAdjPct > 0) {
    adjustmentPct      = blendedAdjPct;
    estimatedRebateRs  = baseCost * (blendedAdjPct / 100);
    rebateApplies      = estimatedRebateRs > 1;
  }

  return {
    applies, rebateApplies,
    pfAvg, pfMin,
    estimatedExtraRs, estimatedRebateRs,
    adjustmentPct,
    sustainedEvents: events.length,
    sustainedMinutes,
    events,
  };
}

export function dailyPenaltyApplies(rows: TelemetryRow[]): {
  applies: boolean; pfAvg: number; estimatedExtraRs: number;
} {
  const p = evaluatePenalty(rows);
  return { applies: p.applies, pfAvg: p.pfAvg, estimatedExtraRs: p.estimatedExtraRs };
}

/* -------------------------------------------------------------------------- */
/*  Meter-wise breakdown                                                     */
/* -------------------------------------------------------------------------- */

export interface MeterRollup {
  deviceId:          string;
  meterSerial:       string;
  location:          string;
  kwh:               number;
  costRs:            number;
  kwAvg:             number;
  pfAvg:             number;
  capacityFactorPct: number;
  loadFactorPct:     number;
  pctOfTotal:        number;
  samples:           number;
}

export function meterwiseBreakdown(rows: TelemetryRow[]): MeterRollup[] {
  const groups = new Map<string, TelemetryRow[]>();
  for (const r of rows) {
    const k    = r.deviceId || r.meterSerial || 'unknown';
    const list = groups.get(k);
    if (list) list.push(r);
    else groups.set(k, [r]);
  }

  const rollups = Array.from(groups.entries()).map(([id, rs]) => {
    const sorted = rs.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    return {
      deviceId:          id,
      meterSerial:       sorted[0].meterSerial,
      location:          sorted[0].location,
      kwh:               energyDelivered(sorted),
      costRs:            costRupees(sorted),
      kwAvg:             mean(sorted.map((r) => safe(r.kwTotal))),
      pfAvg:             mean(sorted.map((r) => powerFactor(r))),
      capacityFactorPct: capacityFactor(sorted),
      loadFactorPct:     loadFactor(sorted),
      pctOfTotal:        0,
      samples:           sorted.length,
    };
  });

  const totalKwh = rollups.reduce((a, m) => a + m.kwh, 0) || 1;
  return rollups
    .map((m) => ({ ...m, pctOfTotal: (m.kwh / totalKwh) * 100 }))
    .sort((a, b) => b.kwh - a.kwh);
}

/* -------------------------------------------------------------------------- */
/*  ToD energy buckets (for stacked bar chart)                               */
/* -------------------------------------------------------------------------- */

export interface TodBucket {
  label:   string;
  ts:      number;
  offPeak: number;
  normal:  number;
  peak:    number;
}

/**
 * kVAh delivered for a filtered (per-ToD-period) row slice — Telangana only.
 *
 * WHY NOT apparentEnergyKvah (VAh register delta):
 *   energyVAh is a cumulative running register that grows continuously across
 *   ALL ToD periods.  When rows are pre-filtered to a single period (e.g. only
 *   NORMAL rows), the delta between the first and last row in that slice spans
 *   across day boundaries and non-contiguous time windows, capturing VAh that
 *   accumulated during other periods — yielding a dramatically wrong value.
 *   In practice the fallback path in apparentEnergyKvah (trapezoidal of vaTotal)
 *   fires instead, and since vaTotal at this site holds Wh-equivalent values,
 *   the result is kWh not kVAh — matching what the user observed.
 *
 *   billing.ts kvahDelivered() uses trapezoidal integration of kvaTotal for
 *   exactly this reason (see its comment block).  We mirror that here so the
 *   chart bars and MIN/AVG/PEAK tiles are consistent with the billing engine.
 *
 * MAX_INTEGRATION_GAP_H (1 h) guard skips day-boundary holes between the last
 * row of one day's period window and the first row of the next — same guard
 * used in billing.ts kvahDelivered() and energyDelivered().
 */
function kvahTrapezoid(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;
  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  let kvah = 0;
  for (let i = 1; i < sorted.length; i++) {
    const dtH = (sorted[i].timestamp.getTime() - sorted[i - 1].timestamp.getTime()) / 3_600_000;
    if (dtH <= 0 || dtH > 1) continue; // skip gaps >1 h (day boundaries / outages)
    kvah += ((safe(sorted[i - 1].kvaTotal) + safe(sorted[i].kvaTotal)) / 2) * dtH;
  }
  return kvah;
}

export function energyByTodBuckets(rows: TelemetryRow[], granularity: Granularity, useKvah = false): TodBucket[] {
  if (!rows.length) return [];

  const groups = new Map<string, TelemetryRow[]>();
  for (const r of rows) {
    const k    = bucketKey(r.timestamp, granularity);
    const list = groups.get(k);
    if (list) list.push(r);
    else groups.set(k, [r]);
  }

  // Select the correct energy function once.
  // kVAh (Telangana): trapezoidal integration of kvaTotal — mirrors billing.ts
  //   kvahDelivered() so chart values match the billing engine exactly.
  //   apparentEnergyKvah() (VAh register delta) is intentionally NOT used here
  //   because the per-period filtered rows are time-discontinuous; the register
  //   delta crosses day boundaries and other periods, returning wrong values.
  // kWh (West Bengal): unchanged — energyDelivered() as before.
  const energyFn = useKvah ? kvahTrapezoid : energyDelivered;

  const raw: Array<{ key: string; ts: number; offPeak: number; normal: number; peak: number }> = [];

  for (const [key, bucketRows] of groups) {
    const sorted    = [...bucketRows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    raw.push({
      key,
      ts:      bucketRows[0].timestamp.getTime(),
      offPeak: energyFn(sorted.filter((r) => r.todPeriod === 'OFF-PEAK')),
      normal:  energyFn(sorted.filter((r) => r.todPeriod === 'NORMAL')),
      peak:    energyFn(sorted.filter((r) => r.todPeriod === 'PEAK')),
    });
  }

  // Clamp each channel to its 99.5th-percentile ceiling
  const clampChannel = (vals: number[]): number[] => {
    if (vals.length < 4) return vals;
    const nonZero = vals.filter((v) => v > 0);
    if (!nonZero.length) return vals;
    const ceiling = percentile(nonZero, 99.5);
    return ceiling > 0 ? vals.map((v) => (v > ceiling ? ceiling : v)) : vals;
  };

  const op = clampChannel(raw.map((b) => b.offPeak));
  const n  = clampChannel(raw.map((b) => b.normal));
  const p  = clampChannel(raw.map((b) => b.peak));

  return raw
    .map((b, i) => ({
      label:   bucketLabel(b.key, granularity),
      ts:      b.ts,
      offPeak: Number(op[i].toFixed(3)),
      normal:  Number(n[i].toFixed(3)),
      peak:    Number(p[i].toFixed(3)),
    }))
    .sort((a, b) => a.ts - b.ts);
}